import { useState, useCallback, useMemo, memo } from "react";
import { useNavigate } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";

const cache = new Map();
const fetchPreview = async (slug) => {
  if (!cache.has(slug)) cache.set(slug, api.get(`/articles/${slug}/preview`).then((r) => r.data).catch(() => null));
  return cache.get(slug);
};

export const useWikiLinks = () => {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);

  const onClick = useCallback((e) => {
    const sp = e.target.closest(".spoiler:not(.revealed)");
    if (sp && !sp.closest(".spoilers-revealed")) {
      e.preventDefault();
      sp.classList.add("revealed");
      return;
    }
    const a = e.target.closest("a");
    const href = a?.getAttribute("href");
    if (href && href.startsWith("/") && !a.target) {
      e.preventDefault();
      setPreview(null);
      navigate(href);
    }
  }, [navigate]);

  const onMouseOver = useCallback(async (e) => {
    const a = e.target.closest("a[data-slug]");
    if (!a) return;
    const sp = a.closest(".spoiler:not(.revealed)");
    if (sp && !sp.closest(".spoilers-revealed")) return;
    const r = a.getBoundingClientRect();
    const slug = a.dataset.slug;
    setPreview({ slug, x: r.left, y: r.bottom });
    const data = await fetchPreview(slug);
    setPreview((p) => (p && p.slug === slug ? { ...p, data } : p));
  }, []);

  const onMouseOut = useCallback((e) => {
    if (e.target.closest("a[data-slug]")) setPreview(null);
  }, []);

  return { handlers: { onClick, onMouseOver, onMouseOut }, preview };
};

export const LinkPreview = ({ preview }) => {
  if (!preview?.data) return null;
  const { data } = preview;
  const img = data.infobox_image || data.hero_image;
  const left = Math.min(preview.x, window.innerWidth - 300);
  return (
    <div data-testid="wiki-link-preview" style={{ left, top: preview.y + 10 }} className={`fixed z-50 w-72 vellum pointer-events-none rise theme-${data.theme || "neutral"}`}>
      {img && <img src={imgUrl(img)} alt="" className="w-full h-28 object-cover border-b border-[#c9a227]/60" />}
      <div className="p-4">
        <p className="eyebrow">{data.section_name}</p>
        <p className="font-heading font-bold text-[#2d1b0e]">{data.title}</p>
        {data.summary && <p className="text-sm mt-1 text-[#4a3a2a] line-clamp-3">{data.summary}</p>}
      </div>
    </div>
  );
};

// Stable __html object so React 19 does not re-write the DOM (and break link clicks) on every re-render
export const RichHtml = memo(({ html, as: Tag = "div", ...rest }) => {
  const inner = useMemo(() => ({ __html: html || "" }), [html]);
  return <Tag {...rest} dangerouslySetInnerHTML={inner} />;
});

export const prepareHtml = (html, stripFirstLetter, isUnlocked) => {
  const doc = new DOMParser().parseFromString(`<div>${html || ""}</div>`, "text/html");
  const root = doc.body.firstChild;
  root.querySelectorAll("a[href]").forEach((a) => {
    const h = a.getAttribute("href");
    if (h.startsWith("/wiki/")) {
      a.classList.add("wiki-link");
      a.dataset.slug = h.slice(6);
      a.removeAttribute("target");
    } else if (/^https?:/i.test(h)) {
      a.target = "_blank";
      a.rel = "noopener noreferrer";
    }
  });
  root.querySelectorAll("span[data-spoiler]").forEach((s) => {
    if (isUnlocked?.(s.dataset.bookId, s.dataset.book)) {
      s.classList.add("revealed", "unlocked");
      s.setAttribute("title", `Spoiler for ${s.dataset.book}, shown because you've finished it`);
    } else s.setAttribute("title", `Spoiler${s.dataset.book ? ` for ${s.dataset.book}` : ""} — tap to reveal`);
  });
  if (stripFirstLetter) {
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let n;
    while ((n = walker.nextNode())) {
      const i = n.nodeValue.search(/[\p{L}\p{N}]/u);
      if (i >= 0) {
        n.nodeValue = n.nodeValue.slice(0, i) + n.nodeValue.slice(i + 1);
        break;
      }
    }
  }
  return root.innerHTML;
};
